// =============================================================================
// StaffDocumentsModal — documents attached to a Zephyr Staff record (db-208)
// =============================================================================
// SuperUsers only (the button that opens this is shown only to SuperUsers, and the
// private 'staff-docs' bucket's policies refuse everyone else in the database).
// List + upload (PDF / JPG / PNG, max 5 MB) + open via a short-lived signed URL.
// No remove and no overwrite (no-delete convention) — same as the DFV Service
// Documents window (db-128). Each upload writes an audit entry.
// =============================================================================
import React from "react";
import { getStaffDocSignedUrl, listStaffDocs, uploadStaffDoc } from "../../data/storage";
import { staffDocContentType, staffDocDisplayName, staffDocError } from "../../services/staffDocs";
import { logAudit } from "../../utils/auditLog";
import { ModalBackdrop } from "../form/FormUI.jsx";

// `onCountChange(n)` (optional) is told the number of documents after every load, so the
// button that opened this window can keep its "(n)" count up to date after an upload.
export function StaffDocumentsModal({ profileId, staffName, onClose, onCountChange }) {
  const [docs, setDocs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [uploading, setUploading] = React.useState(false);
  const [err, setErr] = React.useState("");
  // Held in a ref so a new callback identity on each parent render can't re-trigger the load.
  const onCountChangeRef = React.useRef(onCountChange);
  onCountChangeRef.current = onCountChange;

  const reload = React.useCallback(
    async function () {
      setLoading(true);
      const rows = await listStaffDocs(profileId);
      setDocs(rows);
      setLoading(false);
      onCountChangeRef.current?.((rows || []).length);
    },
    [profileId]
  );

  React.useEffect(
    function () {
      reload();
    },
    [reload]
  );

  async function onUpload(e) {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file after an error
    if (!file) return;
    const problem = staffDocError(file);
    if (problem) {
      setErr(problem);
      return;
    }
    setErr("");
    setUploading(true);
    const { error, path } = await uploadStaffDoc(profileId, file, staffDocContentType(file));
    setUploading(false);
    if (error) {
      setErr("Upload failed — please try again.");
      return;
    }
    logAudit("staff_doc_uploaded", { profile_id: profileId, path });
    await reload();
  }

  async function openDoc(path) {
    const url = await getStaffDocSignedUrl(path);
    if (url) window.open(url, "_blank", "noopener,noreferrer");
    else setErr("Could not open the file.");
  }

  return (
    <ModalBackdrop onClose={onClose} portal>
      <div
        role="dialog"
        aria-label="Staff documents"
        style={{
          background: "#FFFFFF",
          borderRadius: 14,
          width: "calc(100vw - 40px)",
          maxWidth: 640,
          maxHeight: "calc(100vh - 60px)",
          overflowY: "auto",
          boxShadow: "0 8px 40px rgba(0,0,0,0.2)",
        }}
      >
        <div className="flex items-center justify-between py-3 px-5 border-b border-zephyr-border">
          <span className="text-[15px] font-bold text-zephyr-teal-dark">
            Documents{staffName ? ` — ${staffName}` : ""}
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="bg-transparent border-0 text-[22px] leading-none cursor-pointer text-zephyr-muted"
            style={{ fontFamily: "inherit" }}
          >
            ×
          </button>
        </div>
        <div className="p-5">
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <label
              className="bg-zephyr-teal-light rounded-[7px] py-[7px] px-4 text-[13px] cursor-pointer font-semibold text-zephyr-teal-dark"
              style={{ border: "1px solid #00897B" }}
            >
              {uploading ? "Uploading…" : "＋ Upload document"}
              <input
                type="file"
                accept="application/pdf,.pdf,image/jpeg,.jpg,.jpeg,image/png,.png"
                onChange={onUpload}
                disabled={uploading}
                className="hidden"
                data-testid="staff-doc-input"
              />
            </label>
            <span className="text-[12px] text-zephyr-muted">
              PDF, JPG or PNG, up to 5 MB. SuperUsers only.
            </span>
          </div>
          {err && <div className="text-[13px] text-zephyr-red font-semibold mb-2">{err}</div>}
          {loading ? (
            <div className="text-[13px] text-zephyr-muted">Loading…</div>
          ) : docs.length === 0 ? (
            <div className="text-[13px] text-zephyr-muted italic">No documents uploaded yet.</div>
          ) : (
            <div className="border border-zephyr-border rounded-[8px] overflow-hidden">
              {docs.map(function (d, di) {
                return (
                  <div
                    key={d.path}
                    className="flex items-center justify-between gap-2 py-[10px] px-[14px] text-[13px]"
                    style={{ borderTop: di > 0 ? "1px solid #EEF3F2" : "none" }}
                  >
                    <span className="text-zephyr-text overflow-hidden text-ellipsis whitespace-nowrap">
                      📄 {staffDocDisplayName(d.name)}
                    </span>
                    <button
                      type="button"
                      onClick={function () {
                        openDoc(d.path);
                      }}
                      className="bg-transparent rounded-[6px] py-1 px-[12px] text-[12px] cursor-pointer font-semibold text-zephyr-teal-dark shrink-0"
                      style={{ border: "1px solid #00897B", fontFamily: "inherit" }}
                    >
                      Open
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </ModalBackdrop>
  );
}
